import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import LinkedText from '../LinkedText';

const AboutModal = ({ storyData }) => {
    const navigate = useNavigate();
    const modalRef = useRef(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    useEffect(() => {
        if (isLightboxOpen && modalRef.current) {
            modalRef.current.style.overflow = 'hidden';
        } else if (modalRef.current) {
            modalRef.current.style.overflow = 'auto';
        }
    }, [isLightboxOpen]);

    const handleClose = useCallback(() => {
        navigate('/');
    }, [navigate]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const openLightbox = (index) => {
        setSelectedImageIndex(index);
        setIsLightboxOpen(true);
    };

    const closeLightbox = () => {
        setIsLightboxOpen(false);
        setSelectedImageIndex(null);
    };

    const navigateImage = useCallback((direction) => {
        if (!storyData.gallery) return;

        const mediaItems = storyData.gallery;
        const totalItems = mediaItems.length;
        if (direction === 'next') {
            setSelectedImageIndex((prev) => (prev + 1) % totalItems);
        } else {
            setSelectedImageIndex((prev) => (prev - 1 + totalItems) % totalItems);
        }
    }, [storyData.gallery]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (isLightboxOpen) {
                    closeLightbox();
                } else {
                    handleClose();
                }
            }
            if (isLightboxOpen) {
                if (e.key === 'ArrowLeft') {
                    navigateImage('prev');
                } else if (e.key === 'ArrowRight') {
                    navigateImage('next');
                }
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isLightboxOpen, selectedImageIndex, handleClose, navigateImage]);

    if (!storyData) return null;

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/90 backdrop-blur-md animate-fade-in"
                onClick={handleBackdropClick}
            >
                <div
                    ref={modalRef}
                    className="relative w-full max-w-6xl bg-zinc-950 my-0 md:my-8 rounded-none md:rounded-2xl shadow-2xl animate-slide-up overflow-hidden border border-white/10"
                >
                    <button
                        onClick={handleClose}
                        className="absolute top-6 right-6 z-50 w-12 h-12 flex items-center justify-center bg-black/50 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all duration-300 group"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6 text-white/80 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Immersive Hero Section */}
                    <div className="relative w-full h-[75vh] overflow-hidden">
                        <div
                            className="absolute inset-0 w-full h-full transform scale-105 animate-slow-zoom"
                            style={{
                                background: storyData.heroImage ? `url(${storyData.heroImage}) center/cover` : storyData.gradient,
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-zinc-950" />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />

                        <div className="absolute inset-0 flex flex-col items-center justify-end pb-24 px-6 text-center z-10">
                            <div className="space-y-6 max-w-4xl mx-auto animate-slide-up-fade">
                                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight leading-none">
                                    {storyData.title}
                                </h1>
                            </div>
                        </div>
                    </div>

                    {/* Editorial Content */}
                    <div className="relative bg-zinc-950 px-6 pb-24">
                        <div className="max-w-3xl mx-auto -mt-12 relative z-20">
                            <div className="prose prose-lg prose-invert md:prose-xl mx-auto">
                                <p className="text-xl md:text-2xl leading-relaxed text-gray-300 font-light first-letter:text-5xl first-letter:font-bold first-letter:text-white first-letter:mr-3 first-letter:float-left whitespace-pre-line">
                                    <LinkedText text={storyData.description} />
                                </p>
                            </div>
                        </div>

                        {/* Masonry Gallery */}
                        {storyData.gallery && storyData.gallery.length > 0 && (
                            <div className="max-w-7xl mx-auto mt-24">
                                <div className="flex items-center gap-4 mb-12">
                                    <div className="h-px bg-white/20 flex-1" />
                                    <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight uppercase">Gallery</h2>
                                    <div className="h-px bg-white/20 flex-1" />
                                </div>

                                <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
                                    {storyData.gallery.map((item, index) => (
                                        <div
                                            key={index}
                                            className="break-inside-avoid relative rounded-xl overflow-hidden group cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                                            onClick={() => (item.type === 'image' || item.type === 'video') && openLightbox(index)}
                                        >
                                            {item.type === 'image' ? (
                                                <>
                                                    <img
                                                        src={item.src}
                                                        alt={item.alt}
                                                        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                                                    />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                                                        <p className="text-white font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                                            {item.alt}
                                                        </p>
                                                    </div>
                                                </>
                                            ) : item.type === 'video' ? (
                                                <>
                                                    <video
                                                        src={item.src}
                                                        className="w-full h-auto object-cover"
                                                        muted
                                                        loop
                                                        playsInline
                                                    />
                                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-500 flex items-center justify-center">
                                                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                                            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isLightboxOpen && selectedImageIndex !== null && storyData.gallery && storyData.gallery.length > 0 && (
                <div
                    className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fade-in"
                    onClick={closeLightbox}
                >
                    <button
                        onClick={closeLightbox}
                        className="absolute top-4 right-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        aria-label="Close lightbox"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigateImage('prev');
                        }}
                        className="absolute left-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        aria-label="Previous image"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigateImage('next');
                        }}
                        className="absolute right-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                        aria-label="Next image"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>

                    <div
                        className="relative max-w-7xl max-h-[90vh] w-full h-full flex flex-col items-center justify-center p-8 gap-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {storyData.gallery[selectedImageIndex].type === 'image' ? (
                            <img
                                src={storyData.gallery[selectedImageIndex].src}
                                alt={storyData.gallery[selectedImageIndex].alt}
                                className="max-w-full max-h-full object-contain rounded-lg"
                            />
                        ) : (
                            <video
                                src={storyData.gallery[selectedImageIndex].src}
                                controls
                                autoPlay
                                className="max-w-full max-h-full object-contain rounded-lg"
                            />
                        )}
                        {storyData.gallery[selectedImageIndex].alt && (
                            <div className="text-white text-center text-sm md:text-base bg-black/70 px-6 py-3 rounded-lg max-w-2xl">
                                {storyData.gallery[selectedImageIndex].alt}
                            </div>
                        )}
                    </div>

                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
                        {selectedImageIndex + 1} / {storyData.gallery.length}
                    </div>
                </div>
            )}
        </>
    );
};

export default AboutModal;
